import { useEffect, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api, formatPrice } from "../lib/api";
import { Pencil, Trash2, Plus, X, Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";

const ALL_CATEGORIES = [
  "For Men",
  "For Women",
  "Unisex",
  "For Parties",
  "For Office",
  "For Summer",
  "For Winter",
  "For All Weather",
];

const emptyForm = {
  name: "",
  price_inr: "",
  image_url: "",
  notes: "",
  description: "",
  categories: [],
  is_best_seller: false,
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem("ambdrop_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);

  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | product
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Auth bootstrapping — try saved token
  useEffect(() => {
    if (!token) return;
    setLoading(true);
    api
      .post("/admin/login", { token })
      .then(() => setAuthed(true))
      .catch(() => {
        localStorage.removeItem("ambdrop_admin_token");
        setToken("");
      })
      .finally(() => setLoading(false));
  }, [token]);

  const fetchProducts = () =>
    api.get("/products").then((r) => setProducts(r.data));

  useEffect(() => {
    if (authed) fetchProducts();
  }, [authed]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/login", { token: pwd });
      localStorage.setItem("ambdrop_admin_token", pwd);
      setToken(pwd);
      setAuthed(true);
      toast.success("Welcome back");
    } catch {
      toast.error("Wrong admin token");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("ambdrop_admin_token");
    setToken("");
    setAuthed(false);
    setPwd("");
  };

  const openNew = () => {
    setForm(emptyForm);
    setEditing("new");
  };

  const openEdit = (p) => {
    setForm({
      name: p.name,
      price_inr: p.price_inr,
      image_url: p.image_url,
      notes: p.notes,
      description: p.description || "",
      categories: p.categories || [],
      is_best_seller: !!p.is_best_seller,
    });
    setEditing(p);
  };

  const close = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price_inr || !form.image_url) {
      toast.error("Name, price and image URL are required");
      return;
    }
    setSaving(true);
    const headers = { "X-Admin-Token": token };
    try {
      if (editing === "new") {
        await api.post("/admin/products", form, { headers });
        toast.success("Perfume added");
      } else {
        await api.put(`/admin/products/${editing.id}`, form, { headers });
        toast.success("Perfume updated");
      }
      await fetchProducts();
      close();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`, {
        headers: { "X-Admin-Token": token },
      });
      toast.success("Deleted");
      fetchProducts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const toggleCat = (c) => {
    setForm((f) => ({
      ...f,
      categories: f.categories.includes(c)
        ? f.categories.filter((x) => x !== c)
        : [...f.categories, c],
    }));
  };

  // ---- Login screen ----
  if (!authed) {
    return (
      <div className="min-h-screen">
        <Header />
        <section className="max-w-md mx-auto px-6 md:px-12 py-32">
          <p className="text-[11px] tracking-overline uppercase text-[#D4AF37] mb-4 font-bold">
            Atelier · Restricted
          </p>
          <h1 className="font-serif-display font-light text-5xl text-[#F4EAD9] mb-10">
            Admin <span className="italic">access</span>
          </h1>
          <form data-testid="admin-login-form" onSubmit={login} className="space-y-6">
            <div>
              <label className="text-[11px] tracking-overline uppercase text-[#B0A496] font-bold">
                Admin token
              </label>
              <input
                data-testid="admin-token-input"
                type="password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                className="mt-2 w-full bg-transparent border-b border-[#D4AF37] px-1 py-3 outline-none focus:border-[#F4EAD9]"
                placeholder="Enter your admin token"
                required
              />
            </div>
            <button
              data-testid="admin-login-submit"
              disabled={loading}
              className="inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#B8941F] text-[#15110F] px-8 py-4 text-[11px] tracking-overline uppercase transition-colors disabled:opacity-70 font-bold"
            >
              {loading ? <Loader2 size={16} strokeWidth={1.2} className="animate-spin" /> : null}
              Enter
            </button>
          </form>
        </section>
        <Footer />
      </div>
    );
  }

  // ---- Admin dashboard ----
  return (
    <div data-testid="admin-page" className="min-h-screen">
      <Header />

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 pb-12">
        <div className="flex items-end justify-between gap-6 flex-col md:flex-row">
          <div>
            <p className="text-[11px] tracking-overline uppercase text-[#D4AF37] mb-4 font-bold">
              Atelier · {products.length} fragrances
            </p>
            <h1 className="font-serif-display font-light text-5xl md:text-6xl text-[#F4EAD9] leading-[0.95]">
              The <span className="italic">catalog</span>, in your hands.
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <button
              data-testid="admin-add-new"
              onClick={openNew}
              className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#B8941F] text-[#15110F] px-6 py-3 text-[11px] tracking-overline uppercase font-bold"
            >
              <Plus size={14} strokeWidth={1.2} /> Add perfume
            </button>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#15110F] px-6 py-3 text-[11px] tracking-overline uppercase transition-colors font-bold"
            >
              <LogOut size={14} strokeWidth={1.2} /> Sign out
            </button>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-24">
        <div className="border border-[#2D2622] divide-y divide-[#2D2622]">
          {products.map((p) => (
            <div
              key={p.id}
              data-testid={`admin-row-${p.id}`}
              className="grid grid-cols-12 gap-4 items-center px-4 py-4 hover:bg-[#1F1A17]/60"
            >
              <div className="col-span-2 sm:col-span-1">
                <div className="aspect-square w-14 overflow-hidden bg-[#1F1A17]">
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="col-span-6 sm:col-span-5">
                <p className="font-serif-display text-xl text-[#F4EAD9]">{p.name}</p>
                <p className="text-xs text-[#D4AF37] truncate">{p.notes}</p>
              </div>
              <div className="col-span-4 sm:col-span-3 text-[10px] tracking-overline uppercase text-[#B0A496] font-bold">
                {p.categories.slice(0, 3).join(" · ")}
                {p.is_best_seller && (
                  <span className="ml-2 inline-block bg-[#F4EAD9] text-[#15110F] px-2 py-0.5">
                    Best
                  </span>
                )}
              </div>
              <div className="hidden sm:block sm:col-span-1 text-right text-sm tabular-nums text-[#F4EAD9]">
                {formatPrice(p.price_inr)}
              </div>
              <div className="col-span-12 sm:col-span-2 flex justify-end gap-2">
                <button
                  data-testid={`admin-edit-${p.id}`}
                  onClick={() => openEdit(p)}
                  aria-label="Edit"
                  className="p-2 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#15110F] transition-colors"
                >
                  <Pencil size={14} strokeWidth={1.2} />
                </button>
                <button
                  data-testid={`admin-delete-${p.id}`}
                  onClick={() => remove(p)}
                  aria-label="Delete"
                  className="p-2 border border-[#8B0000] text-[#8B0000] hover:bg-[#8B0000] hover:text-[#15110F] transition-colors"
                >
                  <Trash2 size={14} strokeWidth={1.2} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Editor modal */}
      {editing !== null && (
        <div
          data-testid="admin-editor"
          className="fixed inset-0 z-[60] bg-[#000000]/75 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
          onClick={(e) => e.target === e.currentTarget && close()}
        >
          <div className="bg-[#15110F] w-full max-w-2xl my-8 border border-[#2D2622]">
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#2D2622]">
              <p className="font-serif-display text-2xl text-[#F4EAD9]">
                {editing === "new" ? "New perfume" : `Edit · ${editing.name}`}
              </p>
              <button
                data-testid="admin-editor-close"
                onClick={close}
                aria-label="Close"
                className="text-[#D4AF37]"
              >
                <X size={20} strokeWidth={1.2} />
              </button>
            </div>

            <form onSubmit={save} className="px-6 py-6 space-y-6">
              {/* Image preview */}
              {form.image_url && (
                <div className="aspect-[4/3] overflow-hidden bg-[#1F1A17] border border-[#2D2622]">
                  <img src={form.image_url} alt="preview" className="w-full h-full object-cover" />
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Name">
                  <input
                    data-testid="field-name"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="adm-input"
                    placeholder="Oud Royale"
                  />
                </Field>
                <Field label="Price (INR)">
                  <input
                    data-testid="field-price"
                    required
                    value={form.price_inr}
                    onChange={(e) => setForm({ ...form, price_inr: e.target.value })}
                    className="adm-input"
                    placeholder="14,500"
                  />
                </Field>
              </div>

              <Field label="Image URL">
                <div className="flex gap-2 items-start">
                  <input
                    data-testid="field-image-url"
                    required
                    value={form.image_url}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    className="adm-input flex-1"
                    placeholder="Paste image URL or use Upload →"
                  />
                  <label
                    data-testid="upload-image-btn"
                    className={`shrink-0 inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#15110F] px-4 py-[10px] text-[10px] tracking-overline uppercase font-bold cursor-pointer transition-colors ${uploading ? "opacity-70 pointer-events-none" : ""}`}
                  >
                    {uploading ? "Uploading…" : "Upload"}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        e.target.value = "";
                        if (!f) return;
                        if (f.size > 5 * 1024 * 1024) {
                          toast.error("Image too large — max 5 MB");
                          return;
                        }
                        setUploading(true);
                        try {
                          const fd = new FormData();
                          fd.append("file", f);
                          const r = await api.post("/admin/upload", fd, {
                            headers: {
                              "X-Admin-Token": token,
                              "Content-Type": "multipart/form-data",
                            },
                          });
                          setForm((prev) => ({ ...prev, image_url: r.data.url }));
                          toast.success("Image uploaded");
                        } catch (err) {
                          toast.error(
                            err?.response?.data?.detail || "Upload failed"
                          );
                        } finally {
                          setUploading(false);
                        }
                      }}
                    />
                  </label>
                </div>
              </Field>

              <Field label="Notes (short)">
                <input
                  data-testid="field-notes"
                  required
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="adm-input"
                  placeholder="Smoked Wood, Leather, Agarwood"
                />
              </Field>

              <Field label="Description (long, optional)">
                <textarea
                  data-testid="field-description"
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="adm-input"
                />
              </Field>

              <div>
                <p className="text-[11px] tracking-overline uppercase text-[#B0A496] mb-3 font-bold">
                  Categories
                </p>
                <div className="flex flex-wrap gap-2">
                  {ALL_CATEGORIES.map((c) => {
                    const active = form.categories.includes(c);
                    return (
                      <button
                        type="button"
                        key={c}
                        data-testid={`cat-toggle-${c.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => toggleCat(c)}
                        className={`px-3 py-2 text-[10px] tracking-overline uppercase font-bold border transition-colors ${
                          active
                            ? "bg-[#D4AF37] text-[#15110F] border-[#D4AF37]"
                            : "border-[#2D2622] text-[#B0A496] hover:border-[#D4AF37] hover:text-[#F4EAD9]"
                        }`}
                      >
                        {c}
                      </button>
                    );
                  })}
                </div>
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  data-testid="field-best-seller"
                  type="checkbox"
                  checked={form.is_best_seller}
                  onChange={(e) =>
                    setForm({ ...form, is_best_seller: e.target.checked })
                  }
                  className="h-4 w-4 accent-[#D4AF37]"
                />
                <span className="text-[11px] tracking-overline uppercase text-[#F4EAD9] font-bold">
                  Mark as best seller
                </span>
              </label>

              <div className="flex items-center gap-4 pt-4 border-t border-[#2D2622]">
                <button
                  data-testid="admin-save"
                  disabled={saving}
                  className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#B8941F] text-[#15110F] px-8 py-3 text-[11px] tracking-overline uppercase disabled:opacity-70 font-bold"
                >
                  {saving && <Loader2 size={14} strokeWidth={1.2} className="animate-spin" />}
                  {editing === "new" ? "Add perfume" : "Save changes"}
                </button>
                <button
                  type="button"
                  data-testid="admin-cancel"
                  onClick={close}
                  className="editorial-link text-[11px] tracking-overline uppercase text-[#B0A496] font-bold"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />

      <style>{`
        .adm-input {
          width: 100%;
          background: transparent;
          border: 1px solid #2D2622;
          padding: 10px 12px;
          font-size: 14px;
          color: #F4EAD9;
          outline: none;
          font-family: 'Manrope', sans-serif;
        }
        .adm-input:focus { border-color: #D4AF37; }
      `}</style>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <p className="text-[11px] tracking-overline uppercase text-[#B0A496] mb-2 font-bold">{label}</p>
    {children}
  </div>
);

export default Admin;
